// CONTRACT:C1-MAP-CLIENT.1.0
// COP — ALERTS PANEL (OPERATE): the operator's alert list with mark-read-on-click,
// per-row dismiss (×) + clear-all.
//
// A small, calm list of the cleared-VISIBLE `alert`-kind entities the host painted
// (status.alertLog — built off the engine's alert subscription, so it is the visible set
// ONLY). Clicking a row ACKNOWLEDGES it (handle.markAlertRead → the row dims to read);
// the row's × DISMISSES that one alert (handle.dismissAlert — also quiets the
// VerdictGlyph); the header CLEAR ALL button dismisses every visible alert from the log.
// Read/cleared state is persisted PER USER by the host (I8 settings store) — it survives
// a reload; a re-emitted alert re-appears unread (re-fired = attention).
//
// GLOMAR (rule 3): every row here is over the CLEARED-VISIBLE set only. A withheld alert is
// never in status.alertLog (the host built it off the painted set), so it can be neither
// listed, counted, nor "cleared" — byte-and-timing identical to a non-existent one. Clearing
// an alert is a UI self-scope dismiss, NOT a denial: the underlying object stays in the
// registry/on the map and a re-emit re-surfaces it (unread). No "hidden alert" denominator.
//
// React stays OUT of the render loop: this reads the immutable status snapshot + calls the
// CopHandle seam. No host, no MapLibre, no engine imports. Themed via design tokens only.

import type { CopStatus, CopHandle } from "../host/cop.ts";

/** APP-6 affiliation → the shell's --status-* token (Rule 2: hue = affiliation only). */
function affDotColor(aff: string | undefined): string {
  switch ((aff ?? "").toLowerCase()) {
    case "hostile":
      return "var(--status-hostile, #FF5256)";
    case "friend":
    case "friendly":
      return "var(--status-friendly, #4DA6FF)";
    case "neutral":
      return "var(--status-neutral, #46D98E)";
    case "pending":
      return "var(--status-pending, #FF9E40)";
    case "unknown":
    default:
      return "var(--status-unknown, #F2C14E)";
  }
}

export interface AlertsPanelProps {
  status: CopStatus;
  handle: CopHandle;
  /** Optional: select + fly the map to the alerting entity (wired to handle.select in App.tsx).
   *  Absent ⇒ the row label is inert (no dead button). */
  onSelect?: (id: string) => void;
}

export function AlertsPanel({ status, handle, onSelect }: AlertsPanelProps): JSX.Element {
  const alerts = status.alertLog;
  const unread = alerts.reduce((n, a) => (a.read ? n : n + 1), 0);

  // BULK MARK-READ: acknowledge every currently-UNREAD alert. Built on the existing
  // per-alert `markAlertRead` seam (no new host method) — a UI acknowledgement, not a
  // security decision. Glomar-safe: it only touches the cleared-visible alertLog.
  const markAllRead = (): void => {
    for (const a of alerts) {
      if (!a.read) handle.markAlertRead(a.id);
    }
  };

  return (
    <section
      className="panel alerts-panel"
      aria-label="Alerts"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--spacing-sm, 8px)",
        padding: "var(--spacing-md, 12px)",
        fontFamily: "var(--typography-family-ui, system-ui, sans-serif)",
        color: "var(--ui-text, #c7d0db)",
        // BUGFIX (item 4): constrain the panel to its OWN width so the header controls can
        // never be pushed off-screen by a wide alert-list row. Without this, the flex `<ul>`
        // rows sized to the longest (nowrap) label and grew the whole section to ~3000px,
        // dragging "MARK ALL READ" / "CLEAR ALL" out of view (measured x=2762 in a 1332px
        // viewport). `min-width:0` lets the flex children shrink; `overflow:hidden` clips.
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontFamily: "var(--typography-family-mono, ui-monospace, monospace)",
            fontSize: "var(--typography-scale-xs, 11px)",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--ui-text-dim, #8b96a3)",
            // Shrink + ellipsis so the title never elbows the controls off-screen.
            flex: "0 1 auto",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          ALERTS{unread > 0 ? ` · ${unread} unread` : ""}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: "0 0 auto" }}>
          <button
            type="button"
            aria-label="Mark all alerts read"
            title="Acknowledge every visible alert (dims to read; stays in the log)"
            disabled={unread === 0}
            onClick={markAllRead}
            style={{
              background: "none",
              cursor: unread === 0 ? "default" : "pointer",
              border: "1px solid var(--ui-hairline, rgba(255,255,255,0.08))",
              borderRadius: "8px",
              padding: "2px 10px",
              fontFamily: "var(--typography-family-mono, ui-monospace, monospace)",
              fontSize: "var(--typography-scale-xs, 11px)",
              letterSpacing: "0.06em",
              whiteSpace: "nowrap",
              color: unread === 0 ? "var(--ui-text-faint, #5a6571)" : "var(--ui-text-dim, #8b96a3)",
              opacity: unread === 0 ? 0.5 : 1,
            }}
          >
            MARK ALL READ
          </button>
          <button
            type="button"
            aria-label="Clear all alerts"
            title="Dismiss every visible alert from this log (re-emit re-surfaces it)"
            disabled={alerts.length === 0}
            onClick={() => handle.clearAllAlerts()}
            style={{
              background: "none",
              cursor: alerts.length === 0 ? "default" : "pointer",
              border: "1px solid var(--ui-hairline, rgba(255,255,255,0.08))",
              borderRadius: "8px",
              padding: "2px 10px",
              fontFamily: "var(--typography-family-mono, ui-monospace, monospace)",
              fontSize: "var(--typography-scale-xs, 11px)",
              letterSpacing: "0.06em",
              whiteSpace: "nowrap",
              color: alerts.length === 0 ? "var(--ui-text-faint, #5a6571)" : "var(--ui-text-dim, #8b96a3)",
              opacity: alerts.length === 0 ? 0.5 : 1,
            }}
          >
            CLEAR ALL
          </button>
        </div>
      </header>

      {alerts.length === 0 ? (
        <div
          style={{
            color: "var(--ui-text-faint, #5a6571)",
            fontSize: "var(--typography-scale-sm, 13px)",
            padding: "var(--spacing-sm, 8px) 0",
          }}
        >
          No active alerts in your picture.
        </div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "4px", width: "100%", maxWidth: "100%", minWidth: 0 }}>
          {alerts.map((a) => (
            <li
              key={a.id}
              style={{
                minWidth: 0,
                maxWidth: "100%",
                display: "flex",
                alignItems: "stretch",
                gap: "4px",
              }}
            >
              <button
                type="button"
                aria-label={`${a.read ? "Read" : "Unread"} alert: ${a.label}. Click to ${a.read ? "view" : "mark read"}.`}
                aria-pressed={a.read}
                title={a.read ? "Read — click to locate on map" : "Click to mark read (and locate)"}
                onClick={() => {
                  // Mark-read-on-click (the ack), then locate the alerting entity if a seam is wired.
                  handle.markAlertRead(a.id);
                  onSelect?.(a.id);
                }}
                style={{
                  flex: "1 1 auto",
                  // min-width:0 lets the label child shrink so its ellipsis engages instead of
                  // forcing the row (and the whole panel) wide (item 4 root cause).
                  minWidth: 0,
                  boxSizing: "border-box",
                  textAlign: "left",
                  background: a.read ? "transparent" : "var(--ui-surface-raised, rgba(255,255,255,0.03))",
                  cursor: "pointer",
                  border: "1px solid var(--ui-hairline, rgba(255,255,255,0.08))",
                  borderRadius: "8px",
                  padding: "6px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  opacity: a.read ? 0.55 : 1,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    flex: "0 0 auto",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: affDotColor(a.affiliation),
                  }}
                />
                <span
                  style={{
                    flex: "1 1 auto",
                    fontWeight: a.read ? 400 : 600,
                    fontSize: "var(--typography-scale-sm, 13px)",
                    color: "var(--ui-text, #c7d0db)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {a.label}
                </span>
                <span
                  style={{
                    flex: "0 0 auto",
                    fontFamily: "var(--typography-family-mono, ui-monospace, monospace)",
                    fontSize: "var(--typography-scale-xs, 11px)",
                    letterSpacing: "0.06em",
                    color: "var(--ui-text-dim, #8b96a3)",
                  }}
                >
                  {a.classification}
                </span>
                <span
                  style={{
                    flex: "0 0 auto",
                    fontFamily: "var(--typography-family-mono, ui-monospace, monospace)",
                    fontSize: "var(--typography-scale-xs, 11px)",
                    color: "var(--ui-text-faint, #5a6571)",
                  }}
                >
                  {a.time}
                </span>
              </button>
              {/* Per-row DISMISS (×) — a self-scope view dismiss, not a denial: the object
                  stays on the map; a re-emit re-surfaces it (unread). A separate sibling
                  button (never nested — invalid HTML) so row-click still means mark-read. */}
              <button
                type="button"
                aria-label={`Dismiss alert: ${a.label}`}
                title="Dismiss from this log (a re-emit re-surfaces it)"
                onClick={() => handle.dismissAlert(a.id)}
                style={{
                  flex: "0 0 auto",
                  alignSelf: "center",
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  background: "none",
                  cursor: "pointer",
                  border: "1px solid var(--ui-hairline, rgba(255,255,255,0.08))",
                  borderRadius: "8px",
                  fontFamily: "var(--typography-family-mono, ui-monospace, monospace)",
                  fontSize: "var(--typography-scale-sm, 13px)",
                  lineHeight: 1,
                  color: "var(--ui-text-dim, #8b96a3)",
                  opacity: a.read ? 0.55 : 1,
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
